import { useCallback, useEffect, useSyncExternalStore } from 'react'

import { Theme } from '@/shared/types/common.types'
import { STORAGE_KEYS } from '@/shared/utils/constants'

/**
 * Theme hook backed by a MODULE-LEVEL shared store (no React provider needed).
 *
 * WHY a shared store: this hook has ~36 consumers, most of which branch on `isDark` / `isLight` to emit
 * Tailwind classes. While the theme lived in a per-consumer `useState`, changing it in ONE component
 * (the navbar toggle, which actually flows through shared/theme/ThemeProvider) flipped the CSS tokens on
 * <html> but left every OTHER consumer rendering the previous theme's classes — dark text on a dark
 * surface (measured 1.03:1 contrast on /balances) until a full page reload.
 *
 * Two sources feed the store, so a change from EITHER theme system reaches every consumer:
 *  1. this hook's own setters (toggleTheme / setLightTheme / setDarkTheme / applyTheme), and
 *  2. a MutationObserver on <html> — shared/theme/ThemeProvider paints `class` + `data-theme` directly
 *     and never calls this hook, so watching the element is the only in-scope way to observe it.
 */

/** Immutable snapshot handed to `useSyncExternalStore`; its identity changes ONLY on a real state change. */
interface ThemeStoreSnapshot {
  readonly theme: Theme
  readonly isTransitioning: boolean
}

export interface UseThemeResult {
  mode: Theme
  isTransitioning: boolean
  primaryColor: string
  backgroundColor: string
  cardColor: string
  textColor: string
  mutedColor: string
  toggleTheme: () => void
  setLightTheme: () => void
  setDarkTheme: () => void
  applyTheme: (newTheme: Theme) => void
  isLight: boolean
  isDark: boolean
}

const LEGACY_THEME_STORAGE_KEY = 'bat-theme'
const THEME_TRANSITION_DURATION_MS = 200

const themeStoreListeners = new Set<() => void>()

/**
 * `null` means "not resolved yet": the snapshot is built lazily on first read and dropped again when the
 * last consumer unmounts, so a fresh mount always re-reads the persisted theme (boot semantics).
 */
let currentThemeSnapshot: ThemeStoreSnapshot | null = null

let htmlThemeObserver: MutationObserver | null = null

const readPersistedThemeOrDefault = (): Theme => {
  if (typeof window === 'undefined') return 'dark'

  // One-time migration: legacy `bat-theme` key (formerly written by the transaction-matching feature) →
  // canonical `STORAGE_KEYS.THEME` key. Prefer the canonical key when present; otherwise promote the
  // legacy value and delete the old key so the next visit reads the new one.
  const canonical = localStorage.getItem(STORAGE_KEYS.THEME)
  const legacy = localStorage.getItem(LEGACY_THEME_STORAGE_KEY)
  if (!canonical && legacy) {
    localStorage.setItem(STORAGE_KEYS.THEME, legacy)
    localStorage.removeItem(LEGACY_THEME_STORAGE_KEY)
  }

  // Check for saved theme first, then default to dark (as requested by user)
  const savedTheme = localStorage.getItem(STORAGE_KEYS.THEME) as Theme | null
  if (savedTheme) return savedTheme

  return 'dark'
}

const getThemeSnapshot = (): ThemeStoreSnapshot => {
  // Lazy first resolution (and re-resolution after every consumer unmounted).
  currentThemeSnapshot ??= { theme: readPersistedThemeOrDefault(), isTransitioning: false }
  return currentThemeSnapshot
}

/** Stable object for SSR/hydration — `useSyncExternalStore` requires a referentially stable value. */
const SERVER_THEME_SNAPSHOT: ThemeStoreSnapshot = { theme: 'dark', isTransitioning: false }
const getServerThemeSnapshot = (): ThemeStoreSnapshot => SERVER_THEME_SNAPSHOT

const notifyThemeStoreListeners = (): void => {
  themeStoreListeners.forEach((listener) => listener())
}

const setSharedTransitioning = (isTransitioning: boolean): void => {
  const snapshot = getThemeSnapshot()
  if (snapshot.isTransitioning === isTransitioning) return
  currentThemeSnapshot = { theme: snapshot.theme, isTransitioning }
  notifyThemeStoreListeners()
}

const applyThemeToDocument = (newTheme: Theme): void => {
  const root = document.documentElement

  // Add transition class for smooth theme changes
  root.classList.add('theme-transition')
  setSharedTransitioning(true)

  // Remove existing theme classes and attributes
  root.classList.remove('light', 'dark')

  // Add new theme class and data attribute
  root.classList.add(newTheme)
  root.setAttribute('data-theme', newTheme)

  // Save to localStorage
  localStorage.setItem(STORAGE_KEYS.THEME, newTheme)

  // Remove transition class after animation completes
  setTimeout(() => {
    root.classList.remove('theme-transition')
    setSharedTransitioning(false)
  }, THEME_TRANSITION_DURATION_MS)
}

/**
 * Records a theme that is ALREADY painted on <html> (by the other theme system, or by us).
 * Never writes to the DOM — that would fight the painter.
 */
const adoptPaintedTheme = (paintedTheme: Theme): void => {
  const snapshot = getThemeSnapshot()
  if (snapshot.theme === paintedTheme) return
  currentThemeSnapshot = { theme: paintedTheme, isTransitioning: snapshot.isTransitioning }
  notifyThemeStoreListeners()
}

/**
 * Requests a theme change: updates the store AND paints <html> in the SAME synchronous turn.
 * Painting here (instead of in a consumer effect) is what keeps the store and the DOM from ever
 * disagreeing — a pending MutationObserver microtask would otherwise read the not-yet-repainted <html>
 * and revert the store to the previous theme.
 */
const setSharedTheme = (nextTheme: Theme): void => {
  const snapshot = getThemeSnapshot()
  if (snapshot.theme === nextTheme) return
  currentThemeSnapshot = { theme: nextTheme, isTransitioning: snapshot.isTransitioning }
  applyThemeToDocument(nextTheme)
  notifyThemeStoreListeners()
}

/** The theme currently PAINTED on <html> — what the CSS tokens actually render — or null if unpainted. */
const readThemeFromHtmlElement = (): Theme | null => {
  if (typeof document === 'undefined') return null
  const root = document.documentElement
  const dataTheme = root.getAttribute('data-theme')
  if (dataTheme === 'light' || dataTheme === 'dark') return dataTheme
  if (root.classList.contains('light')) return 'light'
  if (root.classList.contains('dark')) return 'dark'
  return null
}

const startHtmlThemeObserver = (): void => {
  if (htmlThemeObserver !== null) return
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return

  // Adopt whatever is already painted: another theme system may have set it before we mounted.
  const paintedTheme = readThemeFromHtmlElement()
  if (paintedTheme) adoptPaintedTheme(paintedTheme)

  htmlThemeObserver = new MutationObserver(() => {
    const currentPaintedTheme = readThemeFromHtmlElement()
    // No-op when unchanged, so our OWN paints (and the transient 'theme-transition' class) cannot loop.
    if (currentPaintedTheme) adoptPaintedTheme(currentPaintedTheme)
  })
  htmlThemeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'data-theme'],
  })
}

const stopHtmlThemeObserver = (): void => {
  htmlThemeObserver?.disconnect()
  htmlThemeObserver = null
}

const subscribeToThemeStore = (listener: () => void): (() => void) => {
  themeStoreListeners.add(listener)
  startHtmlThemeObserver()

  return () => {
    themeStoreListeners.delete(listener)
    if (themeStoreListeners.size === 0) {
      stopHtmlThemeObserver()
      // No consumers left: forget the snapshot so the next mount resolves from localStorage again.
      currentThemeSnapshot = null
    }
  }
}

/** True when <html> + localStorage already carry `theme`, so re-painting would be pure churn. */
const isThemeAlreadyAppliedToDocument = (theme: Theme): boolean => {
  const root = document.documentElement
  return (
    root.getAttribute('data-theme') === theme &&
    root.classList.contains(theme) &&
    localStorage.getItem(STORAGE_KEYS.THEME) === theme
  )
}

// Get theme-specific color values from CSS variables
const getThemeColor = (colorVar: string): string => {
  if (typeof window === 'undefined') return '#6366F1'
  return getComputedStyle(document.documentElement).getPropertyValue(colorVar).trim()
}

export const useTheme = (): UseThemeResult => {
  const { theme, isTransitioning } = useSyncExternalStore(
    subscribeToThemeStore,
    getThemeSnapshot,
    getServerThemeSnapshot,
  )

  useEffect(() => {
    // With many consumers only the first one to run this effect repaints; the rest short-circuit.
    if (isThemeAlreadyAppliedToDocument(theme)) return
    applyThemeToDocument(theme)
  }, [theme])

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent): void => {
      // Only auto-switch if no theme is saved in localStorage
      if (!localStorage.getItem(STORAGE_KEYS.THEME)) {
        setSharedTheme(e.matches ? 'dark' : 'light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const toggleTheme = useCallback(() => {
    setSharedTheme(getThemeSnapshot().theme === 'light' ? 'dark' : 'light')
  }, [])

  const setLightTheme = useCallback(() => setSharedTheme('light'), [])
  const setDarkTheme = useCallback(() => setSharedTheme('dark'), [])

  return {
    mode: theme,
    isTransitioning,
    // Dynamic color values based on current theme
    primaryColor: getThemeColor('--color-crypto-accent'),
    backgroundColor: getThemeColor('--color-crypto-dark'),
    cardColor: getThemeColor('--color-crypto-card'),
    textColor: getThemeColor('--color-crypto-text'),
    mutedColor: getThemeColor('--color-crypto-muted'),
    // Theme control functions
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    applyTheme: applyThemeToDocument,
    // Convenience flags
    isLight: theme === 'light',
    isDark: theme === 'dark',
  }
}
